import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import {
  GoogleAnalyticsService,
  Locale,
  StorageService,
  StorageType,
  TimerService
} from '@rct/lynx-lobbies-shared-lib';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { LYNX_STORAGE_KEYS } from './core/constants/lynx-storage-keys.constant';
import { ModalIconType } from './core/enums/modal-icon-type';
import { LynxAuthService } from './core/services/auth.service';
import { LynxLobbyService } from './core/services/lobby.service';
import { LynxLocaleSettingService } from './core/services/locale-setting.service';
import { LynxLocalizeRouterService } from './core/services/localize-router.service';
import { LynxModalService } from './core/services/modal.service';
import { LynxUserService } from './core/services/user.service';

@Component({
    selector: 'lynx-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroy$ = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _lobbyService: LynxLobbyService,
    private _authService: LynxAuthService,
    private _userService: LynxUserService,
    private _storageService: StorageService,
    private _timerService: TimerService,
    private _translateService: TranslateService,
    private _localizeRouterService: LynxLocalizeRouterService,
    private _modalService: LynxModalService,
    private _elementRef: ElementRef,
    private _renderer: Renderer2,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _localeSettingService: LynxLocaleSettingService
  ) {
    this._setTranslation();
    this._router.events
      .pipe(filter((o) => o instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this._googleAnalyticsService.page(e.url);
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(): boolean {
    return false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this._setAppHeight();
  }

  ngOnInit(): void {
    this._checkIsLoggedIn();
    this._checkIsForceLogout();
    this._setAppHeight();
    this._initLocaleSetting();
  }

  ngAfterViewInit(): void {
    this._changeTheme();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }

  private _initLocaleSetting(): void {
    this._localeSettingService
      .getLocaleSetting$()
      .pipe(takeUntil(this._destroy$))
      .subscribe((localeSetting) => {
        this._lobbyService.updateLocale(localeSetting);
      });
  }

  private _setAppHeight(): void {
    document.documentElement.style.setProperty(
      '--app-height',
      `${window.innerHeight}px`
    );
  }

  private _setTranslation(): void {
    let lastLocale: Locale | string | undefined =
      this._storageService.get(LYNX_STORAGE_KEYS.locale, StorageType.Session) ??
      environment.locale;

    this._translateService.setDefaultLang(lastLocale);

    // prevent lynx font from being applied to aurora  lobby

    this._renderer.addClass(document.body, lastLocale);

    this._translateService.onLangChange
      .pipe(takeUntil(this._destroy$))
      .subscribe((event: LangChangeEvent) => {
        this._renderer.removeClass(document.body, lastLocale);
        lastLocale = event.lang;
        this._renderer.addClass(document.body, lastLocale);
      });
  }

  private _checkIsLoggedIn(): Subscription {
    this._authService.checkAuthentication();

    return this._authService.isLoggedIn$
      .pipe(
        tap((_) => this._timerService.stopAll()),
        takeUntil(this._destroy$)
      )
      .subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this._userService.initBalanceTimer();
          this._authService.initKioskTimer();
        } else {
          this._storageService.lynxClearExceptLobbyAndLocale(LYNX_STORAGE_KEYS);
          this._localizeRouterService.translateRoute('login');
        }
      });
  }

  private _checkIsForceLogout(): Subscription {
    return this._authService.isForceLogout$
      .pipe(
        filter((x) => x),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this._modalService.dispatch({
          icon: ModalIconType.WARNING,
          title: 'Error.SessionExpired.Title',
          content: [
            'Error.SessionExpired.Description.0',
            'Error.SessionExpired.Description.1'
          ],
          data: {
            LobbyID: this._lobbyService.lobbyId
          }
        });
      });
  }

  private _changeTheme(): Subscription {
    this._lobbyService.checkTheme();
    this._lobbyService.checkLobbyTemplateId();

    return this._lobbyService.theme$
      .pipe(takeUntil(this._destroy$))
      .subscribe((theme) => {
        const bodyEl = this._elementRef.nativeElement.ownerDocument.body;
        this._renderer.setProperty(
          bodyEl,
          'id',
          `theme-${theme.colorScheme.toLowerCase()}`
        );
        bodyEl.style.setProperty('--brandMain', theme.brandMainGradient[0]);
        bodyEl.style.setProperty('--brandDark', theme.brandMainGradient[1]);
      });
  }
}
